import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  exchangeTelegramToken,
  login,
  telegramLogin,
  TelegramWidgetUser,
} from '../../api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { TelegramLoginButton, isTelegramConfigured } from './TelegramLoginButton';
import { getInviteInfo, redeemInvite, type InviteInfoDto } from '@/api/invites';
import { extractApiError } from '@/utils/apiError';

type Mode = 'login' | 'signup';

// Stash the JWT (and friends) in one place so the "keep me signed in"
// decision only matters in one spot.
function persistTokens(access: string, refresh?: string, username?: string) {
  localStorage.setItem('jwt', access);
  if (refresh) localStorage.setItem('refresh', refresh);
  if (username) localStorage.setItem('username', username);
}

function parseInviteTokenFromNext(next: string): string | null {
  // Accept /invite/<uuid> with optional trailing slash + query.
  const m = next.match(/^\/invite\/([^/?#]+)/);
  return m ? m[1] : null;
}

const LoginPage = () => {
  const { t } = useT();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // A visitor arriving via an invite link is almost always a NEW user, so
  // start them in signup: the password button then reads «Создать аккаунт…»
  // and the form registers instead of logging in — otherwise a first-timer
  // saw «Войти по логину и паролю» with no hint that this is where they make
  // an account (Alexey 2026-08-21). Same URL parse as `nextTarget` below,
  // inlined because that memo is declared after this state.
  const [mode, setMode] = useState<Mode>(() =>
    parseInviteTokenFromNext(new URLSearchParams(window.location.search).get('next') || '')
      ? 'signup'
      : 'login',
  );

  // Telegram reachability — active probe of oauth.telegram.org BEFORE
  // we reveal the TG block. In OIDC mode the button is a static
  // element that just redirects to oauth.telegram.org on click —
  // without an upfront probe, users in countries where TG is blocked
  // would tap the button and watch the browser hang on TG's DNS
  // timeout. We probe the OIDC discovery doc (same host as the
  // authorize endpoint) with no-cors + a 2s AbortSignal — short
  // enough that the "TG blocked, here's the password fallback"
  // message reaches mobile users well within the 2 s render budget
  // (a 4 s probe left them staring at the shimmer past the point
  // they'd normally bail), tight enough that a blocked TG can't
  // masquerade as a slow one. While probing, the TG block is hidden
  // entirely; unreachable → small fallback hint replaces the divider
  // + button.
  //
  // Single probe covers both modes — widget mode needs telegram.org
  // (the widget JS host), OIDC mode needs oauth.telegram.org (the
  // redirect target); in practice the same network policies block
  // both, so probing the OIDC host is sufficient.
  const telegramConfigured = isTelegramConfigured;
  const [tgReachable, setTgReachable] = useState<null | boolean>(null);
  const telegramContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!telegramConfigured) return;
    let cancelled = false;
    // A short abort (was 2s) false-negatived on slow-but-reachable networks and
    // wrongly disabled login for users who DO have Telegram (Alexey 2026-07-10). A
    // reachable host resolves this fetch in well under a second and returns early, so
    // a large budget costs reachable users nothing — it only makes the "blocked"
    // verdict (which correctly disables login) patient: two 15s attempts (~30s) before
    // we call TG down. During the wait the button renders gray/disabled, not a
    // spinner (see TelegramLoginButton), and an impatient tap surfaces the verdict
    // early — a reachable TG would already have turned the button blue.
    (async () => {
      for (let attempt = 0; attempt < 2 && !cancelled; attempt++) {
        const ctrl = new AbortController();
        const timeoutId = window.setTimeout(() => ctrl.abort(), 15000);
        try {
          await fetch('https://oauth.telegram.org/.well-known/openid-configuration', {
            mode: 'no-cors',
            cache: 'no-store',
            signal: ctrl.signal,
          });
          window.clearTimeout(timeoutId);
          if (!cancelled) setTgReachable(true);
          return;
        } catch {
          window.clearTimeout(timeoutId);
          // fall through to retry; only declare blocked after the last attempt
        }
      }
      if (!cancelled) setTgReachable(false);
    })();
    return () => { cancelled = true; };
  }, [telegramConfigured]);

  // The TG block always renders when configured — its internal
  // state cycles through probing (disabled + spinner) → ready
  // (enabled) → blocked (disabled + VPN hint). Reserving the slot
  // from the first render avoids the layout shift we used to have
  // when the block popped in after the probe finished.

  // Auto-exchange tg_token if present (unauthenticated TG bot
  // deep-link). PrivateRoutes preserves search params on redirect to
  // /login so the token lands here.
  useEffect(() => {
    const tgToken = new URLSearchParams(window.location.search).get('tg_token');
    if (!tgToken) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await exchangeTelegramToken(tgToken);
        if (cancelled) return;
        persistTokens(resp.data.access, resp.data.refresh);
        window.location.href = '/usage/dashboard';
      } catch {
        // Invalid/expired token — user must log in manually
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ``next`` query param — relative same-app paths only (open-redirect
  // protection). If the destination is /invite/<token>, look up the
  // invite info so we can show the zone in the greeting and bypass
  // the redeem round-trip for new accounts.
  const nextTarget = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('next');
    if (!raw) return '/';
    return raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
  }, []);
  const inviteToken = useMemo(() => parseInviteTokenFromNext(nextTarget), [nextTarget]);
  const [inviteInfo, setInviteInfo] = useState<InviteInfoDto | null>(null);
  const [inviteInfoError, setInviteInfoError] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    getInviteInfo(inviteToken).then(
      r => { if (!cancelled) setInviteInfo(r.data); },
      err => { if (!cancelled) setInviteInfoError(extractApiError(err, t('invite.invalid'))); },
    );
    return () => { cancelled = true; };
  }, [inviteToken, t]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await login(username, password);
      persistTokens(resp.data.access, resp.data.refresh, username);
      window.location.href = nextTarget;
    } catch (e: unknown) {
      // Only HTTP 401 means "wrong creds". Anything else (network
      // error, 405 because axios baseURL is empty + post hits the
      // FE nginx, 5xx from the BE) is a real failure that the
      // generic invalidCreds copy hides. Surface the http code so
      // the operator can tell "I typed the wrong password" from
      // "the FE bundle was built without VITE_API_BASE so it can't
      // even reach the BE".
      const err = e as { response?: { status?: number }; message?: string };
      const status = err?.response?.status;
      if (status === 401) {
        setError(t('login.invalidCreds'));
      } else if (status) {
        setError(`${t('login.serverError')} (HTTP ${status})`);
      } else {
        setError(t('login.networkError'));
      }
      setLoading(false);
    }
  }

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteToken) return;
    setError('');
    setLoading(true);
    try {
      const res = await redeemInvite(inviteToken, { username, password });
      persistTokens(res.data.access, res.data.refresh, username);
      window.location.href = `/usage/dashboard?zone=${res.data.zone_id}`;
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
      setLoading(false);
    }
  }

  async function handleTelegramAuth(user: TelegramWidgetUser) {
    setError('');
    setLoading(true);
    try {
      // Pass invite_token so unbound TG users can be bootstrapped
      // into an account by the BE — without it the BE 403s. Also
      // covers the bound-but-not-yet-member case in one round trip.
      const resp = await telegramLogin(user, inviteToken || undefined);
      persistTokens(resp.data.access, resp.data.refresh, user.username);
      window.location.href = nextTarget;
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      if (err?.response?.status === 403 && err?.response?.data?.detail === 'no_binding') {
        setError(t('auth.tgNoBinding'));
      } else {
        setError(t('login.telegramError'));
      }
      setLoading(false);
    }
  }

  const submitting = loading;
  const inSignup = mode === 'signup' && !!inviteToken;
  const showSignupSwitch = !!inviteToken && mode === 'login';
  const showLoginSwitch = inSignup;

  // Password form is collapsed behind a "Войти по логину и паролю"
  // button when TG is offered (reachable or blocked-with-hint), so the
  // page reads as TG-first. When no TG path is configured at all, the
  // form opens by default — there's nothing else on the page to click.
  const [passwordOpen, setPasswordOpen] = useState(!telegramConfigured);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="-mt-[5vh] flex w-full max-w-[340px] flex-col items-center gap-4">
        <img
          src="https://storage.yandexcloud.net/linza-media/static/img/logo_linza.png"
          alt="Linza Metrics"
          className="h-[60px] w-auto"
        />
        <h1 className="-mt-1 text-center text-[22px] font-normal tracking-tight text-foreground">
          {t('login.title')}
        </h1>

        {/* Method-choice hint — only on the initial screen where BOTH the
            Telegram button and the password entry point are offered, so it
            actually frames a choice (Alexey 2026-08-21). */}
        {telegramConfigured && !passwordOpen && (
          <p className="-mt-1 text-center text-sm text-muted-foreground">
            {t('login.chooseMethod')}
          </p>
        )}

        {/* Invite-context greeting — eased in once the BE returns
            invite info so the card doesn't snap into the layout
            after a delay. */}
        {inviteToken && inviteInfo && (
          <div className="w-full animate-in fade-in slide-in-from-top-1 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm duration-300">
            <div className="font-medium text-foreground">
              {t('auth.inviteGreeting', { zone: inviteInfo.zone_name })}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {t('auth.inviteGreetingSub', { role: t(`role.${inviteInfo.role}`) })}
            </div>
          </div>
        )}
        {inviteToken && inviteInfoError && (
          <div className="w-full rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {inviteInfoError}
          </div>
        )}

        {/* Mini App landed here because the user's TG isn't bound to an
            account. Generic /login flash would leave them guessing; the
            banner tells them what's wrong + the password fallback is
            below if they happen to have one. */}
        {(() => {
          const reason = new URLSearchParams(window.location.search).get('reason');
          if (reason === 'miniapp_no_binding') {
            return (
              <div className="w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-100">
                {t('auth.tgNoBinding')}
              </div>
            );
          }
          if (reason === 'miniapp_error') {
            return (
              <div className="w-full rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {t('login.telegramError')}
              </div>
            );
          }
          return null;
        })()}

        {/* Telegram sign-in. Renders ABOVE the password slot when
            the password form is collapsed (primary path on top), and
            BELOW when the form is expanded (focus shifts to the form
            but TG stays one click away). */}
        {telegramConfigured && !passwordOpen && (
          <div ref={telegramContainerRef} className="w-full">
            <TelegramLoginButton onAuth={handleTelegramAuth} reachable={tgReachable} />
          </div>
        )}

        {/* Password path — collapsed behind a button when TG is on
            offer, expanded by default when it isn't. Solid card
            background so the button doesn't blend into the page bg. */}
        {!passwordOpen && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setPasswordOpen(true)}
            className="h-11 w-full gap-2 rounded-[10px] bg-card font-medium text-foreground"
          >
            <KeyRound className="size-4" />
            {inSignup
              ? t('auth.signupWithPassword')
              : t('auth.loginWithPassword')}
          </Button>
        )}

        {/* Expanded password form — floating fields and a primary-
            size submit, NOT wrapped in a card. The card-with-padding
            wrapper made the inputs read as smaller than the TG button
            even though they share the same width; rendering at the
            same visual scale makes them feel like peers. */}
        {passwordOpen && (
          <form onSubmit={inSignup ? handleSignup : handleLogin} className="flex w-full flex-col gap-3">
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
                {error}
              </div>
            )}
            <div>
              <Label
                htmlFor="username"
                className="mb-1 block text-[12px] font-normal uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('auth.usernameLabel')}
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                className="h-11"
              />
            </div>

            <div>
              <Label
                htmlFor="password"
                className="mb-1 block text-[12px] font-normal uppercase tracking-[0.08em] text-muted-foreground"
              >
                {t('auth.passwordLabel')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={inSignup ? 'new-password' : 'current-password'}
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="mt-1 h-11 w-full rounded-[10px] font-semibold"
            >
              {submitting
                ? t('login.submitting')
                : inSignup
                  ? t('auth.signupSubmit')
                  : t('login.submit')}
            </Button>

            {showSignupSwitch && (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {t('auth.noAccountPrompt')}{' '}
                <button
                  type="button"
                  onClick={() => { setError(''); setMode('signup'); }}
                  className="text-foreground underline-offset-2 hover:underline"
                >
                  {t('auth.noAccountCta')}
                </button>
              </p>
            )}
            {showLoginSwitch && (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {t('auth.haveAccountPrompt')}{' '}
                <button
                  type="button"
                  onClick={() => { setError(''); setMode('login'); }}
                  className="text-foreground underline-offset-2 hover:underline"
                >
                  {t('auth.haveAccountCta')}
                </button>
              </p>
            )}
          </form>
        )}

        {/* Divider + TG slot when the form is open — TG slides below
            as an "actually, use Telegram instead" escape hatch. ИЛИ
            divider separates it from the form above so the eye reads
            them as two independent options, not one continuous flow. */}
        {telegramConfigured && passwordOpen && (
          <>
            <div className="flex w-full items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span>{t('auth.or')}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div ref={telegramContainerRef} className="w-full">
              <TelegramLoginButton onAuth={handleTelegramAuth} reachable={tgReachable} />
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default LoginPage;
